import React from "react";
import { Typography } from "@mui/material";
import Paper from "@components/Paper/Paper";
import type { SideDrawerAttachments } from "@services/movement-services/types";
import { useTranslation } from "react-i18next";
import type { MovementServiceStatusDetails } from "../types";
import MarineInformationSection from "./MarineInformationSection";

const LANG_BASE = "vessel-call.movement-services.preview.marine-information";
const TEST_ID = "movement-service-marine-information";

const hasMarineInformation = (decision: MovementServiceStatusDetails) =>
  Boolean(
    decision.externalNotes?.trim() ||
      decision.externalAttachments?.length ||
      decision.internalNotes?.trim() ||
      decision.internalAttachments?.length,
  );

interface MarineInformationProps {
  statusDetails: MovementServiceStatusDetails[];
  onDownload: (attachment: SideDrawerAttachments) => void;
  onPreview: (attachment: SideDrawerAttachments) => void;
}

const MarineInformation: React.FC<MarineInformationProps> = ({
  statusDetails,
  onDownload,
  onPreview,
}) => {
  const { t } = useTranslation();

  const visibleDecisions = statusDetails.filter(hasMarineInformation);
  const approvalDecision = visibleDecisions.find(
    (decision) =>
      decision.status === "APPROVED" &&
      (decision.submittedByRole === "marine-operator" ||
        decision.submittedByRole === "marine-supervisor"),
  );
  const closureDecision = visibleDecisions.find((decision) => decision.status === "CLOSED");

  if (!approvalDecision && !closureDecision) return null;

  return (
    <Paper variant="outlined">
      <div className="flex flex-col gap-3" data-testid={TEST_ID}>
        <Typography
          component="h2"
          variant="subtitle1"
          className="font-bold"
          sx={{ fontWeight: 600, color: "#111827" }}
        >
          {t(`${LANG_BASE}.title`)}
        </Typography>
        <div className="flex flex-col gap-4">
          <MarineInformationSection
            title={t(`${LANG_BASE}.approval`)}
            decision={approvalDecision}
            testId={`${TEST_ID}-approval`}
            onDownload={onDownload}
            onPreview={onPreview}
          />
          <MarineInformationSection
            title={t(`${LANG_BASE}.closure`)}
            decision={closureDecision}
            testId={`${TEST_ID}-closure`}
            onDownload={onDownload}
            onPreview={onPreview}
          />
        </div>
      </div>
    </Paper>
  );
};

export default MarineInformation;
